import React, { Fragment, useEffect, useState } from "react";
import Dialog from "@material-ui/core/Dialog";
import { useTheme } from "@material-ui/core/styles";
import { useSetState } from "Helpers/Hooks";
import DialogContent from "@material-ui/core/DialogContent";
import Input from "Components/Input/Input";
import Grid from "@material-ui/core/Grid";
import Translator from "Languages/Utility/Translator";
import { read } from "Helpers/LocalStorage";
import showNotify from "Helpers/Notify";
import MuiButton from "Components/MuiButton/MuiButton";
import { DialogActions } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import * as Icons from "react-bootstrap-icons";
import MuiAutoComplete from "Components/MuiAutoComplete/MuiAutoComplete";
import AccountService from "Services/http/endpoints/superadmin/AccountsEndpoints";
import StatusHandler from "Helpers/StatusHandler";
import * as R from "ramda";
import { useDispatch, useSelector } from "react-redux";
import BackDropLoading from "Components/BackDropLoading2/BackDropLoading";
import {
  getAccountingDManualCreditMethod,
  getAccountingDManualCreditType,
} from "Stores/actions";

export default function NewIncCredit({ disabled, getDataAgain }) {
  const { direction } = useTheme();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [formValid, setFormValid] = useState({});
  const [loading, setLoading] = useState(false);
  const [checkValid, setCheckValid] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const { allGroups, creditMethod, creditType } = useSelector((state) => ({
    allGroups: state.saAccounts.allGroups,
    creditMethod: state.saAccounts.creditMethod,
    creditType: state.saAccounts.creditType,
  }));
  const [controls, setControls] = useSetState({
    method: {
      type: "select",
      selectItems: [],
      value: "",
      validation: null,
      display: true,
      col: {
        sm: 4,
        xs: 12
      }
    },
    groups: {
      type: "select",
      selectItems: [],
      value: "",
      validation: null,
      display: true,
      col: {
        sm: 4,
        xs: 12
      }
    },
    type: {
      type: "select",
      selectItems: [],
      value: "",
      validation: null,
      display: true,
      col: {
        sm: 4,
        xs: 12
      }
    },
    price: {
      type: "price",
      value: "",
      validation: {
        required: true,
        isPrice: true,
        notZero: true,
      },
      col: {
        sm: 4,
        xs: 12
      }
    },
    description: {
      type: "text",
      value: "",
      validation: null,
      col: {
        sm: 8,
        xs: 12
      }
    },
  });

  const onShowDialog = () => {
    setShowDialog(true);
  };

  const onEnter = async () => {
    // reset variables
    setControls({ price: { value: "" } });
    setControls({ description: { value: "" } });
    setFormValid({ price: false });
    // get select from api
    await getComboData();
    // fill selects
    if (allGroups) {
      const _groups = allGroups.map((child) => {
        return {
          label: child.Name,
          value: child.Id,
        };
      });
      setControls({
        groups: {
          selectItems: _groups,
          value: _groups[0],
        },
      });
    } else {
      showNotify({ text: t("getDataError"), customClass: "error" });
      onExit();
    }
  };

  const getComboData = async () => {
    return new Promise((resolve) => {
      (async () => {
        try {
          const params = {
            KeyGroup: read("groupAKey"),
            KeyUser: read("userAKey"),
          };
          await dispatch(getAccountingDManualCreditMethod(params));
          await dispatch(getAccountingDManualCreditType(params));
          resolve(true);
        } catch (err) {
          console.error("Promise error:", err);
          resolve(false);
        }
      })();
    });
  };

  useEffect(() => {
    if (creditMethod) {
      const selectItems = creditMethod.map((child) => {
        return {
          label: child.Name,
          value: child.Id,
        };
      });
      setControls({ method: { selectItems } });
      setControls({ method: { value: selectItems[0] } });
      if (selectItems[0].value === 1)
        setControls({ groups: { display: false } });
    }
  }, [creditMethod]);

  useEffect(() => {
    if (creditType) {
      const selectItems = creditType.map((child) => {
        return {
          label: child.Name,
          value: child.Id,
        };
      });
      setControls({ type: { selectItems } });
      setControls({ type: { value: selectItems[0] } });
    }
  }, [creditType]);

  const setValue = (name, value, validation) => {
    setControls({ [name]: { value: value } });
    setFormValid((prev) => ({ ...prev, [name]: validation }));
  };

  const autoCompleteHandler = (e, value) => {
    const controlName = e.target.id.split("-")[0];
    setControls({ [controlName]: { value: value } });
    if (controlName === "method")
      setControls({ groups: { display: value.value === 1 ? false : true } });
  };

  const onFinish = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const { method, groups, type, price, description } = controls;
      if (
        !R.isEmpty(formValid) &&
        Object.values(formValid).every((item) => item === true)
      ) {
        const data = {
          KeyGroup: read("groupAKey"),
          KeyUser: read("userAKey"),
          TypeId: type.value.value,
          MethodId: method.value.value,
          Price: +price.value,
          GroupId: method.value.value === 1 ? 0 : groups.value.value,
          Describ: description.value,
        };
        const response = await AccountService.postAccountingManualCredit(data);
        let err = StatusHandler(response);
        if (!err.isFail) {
          if (response.data.Value.IsError === true) {
            showNotify({
              text: response.data.Value.Describ,
              customClass: "error",
            });
          } else {
            showNotify({
              text: t("mission_accomplished"),
              customClass: "success",
            });
            getDataAgain();
            onExit();
          }
          setTimeout(() => {
            setLoading(false);
          }, 500);
        } else {
          setLoading(false);
        }
      } else {
        setCheckValid(true);
        setLoading(false);
      }
    } catch (err) {
      setLoading(false);
    }
  };

  const onExit = () => {
    setShowDialog(false);
    setLoading(false);
    setCheckValid(false);
  };

  return (
    <>
      <BackDropLoading loading={loading} />
      <MuiButton
        color="primary"
        title={<Translator id="newIncCredit" />}
        innerIcon={<Icons.PlusLg />}
        onClick={onShowDialog}
        disabled={disabled || !read("FormManualCredit").accessRegister}
        hidden={!read("FormManualCredit").accessRegister}
      />
      <Dialog
        dir={direction}
        open={showDialog}
        onClose={onExit}
        TransitionProps={{ onEnter: onEnter, onExit: onExit }}
        fullWidth
        maxWidth="sm"
      >
        <div className="dialogHeader justify-content-start">
          <p>
            <Translator id="newIncCredit" />
          </p>
        </div>
        <DialogContent>
          <Grid container spacing={2}>
            {Object.keys(controls)
            .filter(key => controls[key].display)
            .map((key) => {
              return (
                <Grid item sm={controls[key].col.sm} xs={controls[key].col.xs} key={key}>
                  {controls[key].type === "select" && controls[key].display && (
                    <MuiAutoComplete
                      autoSelect={true}
                      options={controls[key].selectItems}
                      label={<Translator id={key} />}
                      value={controls[key].value}
                      onChange={autoCompleteHandler}
                      id={key}
                    />
                  )}
                </Grid>
              );
            })}
          </Grid>
          <Grid container spacing={2}>
            {Object.keys(controls)
              .filter((key) => controls[key].type !== "select")
              .map((key) => {
                return (
                  <Grid item sm={controls[key].col.sm} xs={controls[key].col.xs} key={key}>
                    {(controls[key].type === "price" ||
                      controls[key].type === "text") && (
                      <Input
                        setValue={setValue.bind(this)}
                        value={controls[key].value}
                        name={key}
                        validation={controls[key].validation}
                        label={<Translator id={key} />}
                        placeholder={t(key)}
                        type={controls[key].type}
                        checkValid={checkValid}
                        endIcon={
                          controls[key].type === "price" ? (
                            <Translator id="moneyUnit" />
                          ) : (
                            ""
                          )
                        }
                      />
                    )}
                  </Grid>
                );
              })}
          </Grid>
        </DialogContent>
        <DialogActions>
          <MuiButton
            color="primary"
            title={<Translator id="button_save" />}
            onClick={onFinish}
          />
          <MuiButton
            onClick={() => setShowDialog(false)}
            title={<Translator id="enseraf" />}
          />
        </DialogActions>
      </Dialog>
    </>
  );
}
